import React, { useState, useEffect } from 'react';
import { Card } from './ui/card';
import { Button } from './ui/button';
import { Badge } from './ui/badge';
import FullScreenEditor from './FullScreenEditor';
import { usePopupContext } from '../contexts/PopupContext';
import ImageUpload from './ui/image-upload';
import { 
  Settings, 
  Users, 
  FileText, 
  BarChart3, 
  Edit3, 
  Save, 
  Plus, 
  Trash2,
  Eye,
  LogOut,
  Shield
} from 'lucide-react';

const AdminDashboard = ({ user, onLogout }) => {
  const [activeTab, setActiveTab] = useState('hero');
  const [heroContent, setHeroContent] = useState(null);
  const [personalContent, setPersonalContent] = useState(null);
  const [projects, setProjects] = useState([]);
  const [blogs, setBlogs] = useState([]);
  const [contactContent, setContactContent] = useState(null);
  const [analytics, setAnalytics] = useState(null);
  const [loading, setLoading] = useState(false);
  const [adsEnabled, setAdsEnabled] = useState(true);
  const [showFullScreenEditor, setShowFullScreenEditor] = useState(false);
  const [editorConfig, setEditorConfig] = useState({
    type: '',
    itemId: ''
  });
  const [siteSettings, setSiteSettings] = useState(null);
  const { showSuccess, showError, showInfo } = usePopupContext();

  const API_BASE = process.env.REACT_APP_BACKEND_URL + '/api';
  const authToken = localStorage.getItem('admin_token');

  const authHeaders = {
    'Authorization': `Bearer ${authToken}`,
    'Content-Type': 'application/json'
  };

  // Load content based on active tab
  useEffect(() => {
    loadContent();
  }, [activeTab]);

  const loadContent = async () => {
    setLoading(true);
    try {
      switch (activeTab) {
        case 'hero':
          const heroRes = await fetch(`${API_BASE}/content/hero`);
          const heroData = await heroRes.json();
          setHeroContent(heroData);
          break;
        case 'personal':
          const personalRes = await fetch(`${API_BASE}/content/personal`);
          const personalData = await personalRes.json();
          setPersonalContent(personalData);
          break;
        case 'projects':
          const projectsRes = await fetch(`${API_BASE}/content/projects`);
          const projectsData = await projectsRes.json();
          setProjects(projectsData);
          break;
        case 'blogs':
          const blogsRes = await fetch(`${API_BASE}/content/blogs`);
          const blogsData = await blogsRes.json();
          setBlogs(blogsData);
          break;
        case 'contact':
          const contactRes = await fetch(`${API_BASE}/content/contact`);
          const contactData = await contactRes.json();
          setContactContent(contactData);
          break;
        case 'analytics':
          const analyticsRes = await fetch(`${API_BASE}/admin/analytics/visitors`, {
            headers: authHeaders
          });
          const analyticsData = await analyticsRes.json();
          setAnalytics(analyticsData);
          
          // Load site settings for ad toggle
          const settingsRes = await fetch(`${API_BASE}/admin/settings`, {
            headers: authHeaders
          });
          const settingsData = await settingsRes.json();
          setSiteSettings(settingsData);
          setAdsEnabled(settingsData.ads_enabled);
          break;
      }
    } catch (error) {
      console.error('Error loading content:', error);
    } finally {
      setLoading(false);
    }
  };
  const toggleAds = async () => {
    try {
      const newSettings = {
        ...siteSettings,
        ads_enabled: !adsEnabled,
        updated_at: new Date().toISOString()
      };
      
      const response = await fetch(`${API_BASE}/admin/settings`, {
        method: 'PUT',
        headers: authHeaders,
        body: JSON.stringify(newSettings)
      });
      
      if (response.ok) {
        setAdsEnabled(!adsEnabled);
        setSiteSettings(newSettings);
        alert(`Ads ${!adsEnabled ? 'enabled' : 'disabled'} successfully!`);
      }
    } catch (error) {
      console.error('Error updating ad settings:', error);
      alert('Error updating settings');
    }
  };

  const saveProject = async (projectData) => {
    setLoading(true);
    try {
      const url = projectData.id && projectData.id !== 'new'
        ? `${API_BASE}/admin/content/projects/${projectData.id}`
        : `${API_BASE}/admin/content/projects`;
      const method = projectData.id && projectData.id !== 'new' ? 'PUT' : 'POST';

      const response = await fetch(url, {
        method,
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${authToken}`
        },
        body: JSON.stringify(projectData)
      });

      if (response.ok) {
        const updatedProject = await response.json();
        if (projectData.id && projectData.id !== 'new') {
          setProjects(projects.map(p => p.id === projectData.id ? updatedProject : p));
        } else {
          setProjects([...projects, updatedProject]);
        }
        await loadContent(); // Refresh the list
      }
    } catch (error) {
      console.error('Error saving project:', error);
    } finally {
      setLoading(false);
    }
  };

  const saveBlog = async (blogData) => {
    setLoading(true);
    try {
      const url = blogData.id && blogData.id !== 'new'
        ? `${API_BASE}/admin/content/blogs/${blogData.id}`
        : `${API_BASE}/admin/content/blogs`;
      
      const method = blogData.id && blogData.id !== 'new' ? 'PUT' : 'POST';
      
      const response = await fetch(url, {
        method,
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${authToken}`
        },
        body: JSON.stringify(blogData)
      });
      
      if (response.ok) {
        const updatedBlog = await response.json();
        if (blogData.id && blogData.id !== 'new') {
          setBlogs(blogs.map(b => b.id === blogData.id ? updatedBlog : b));
        } else {
          setBlogs([...blogs, updatedBlog]);
        }
        await loadContent(); // Refresh the list
      }
    } catch (error) {
      console.error('Error saving blog:', error);
    } finally {
      setLoading(false);
    }
  };

  const saveHeroContent = async () => {
    setLoading(true);
    try {
      const response = await fetch(`${API_BASE}/admin/content/hero`, {
        method: 'PUT',
        headers: authHeaders,
        body: JSON.stringify(heroContent)
      });
      if (response.ok) {
        showSuccess('Hero section content has been updated successfully! Changes are now live on the website.', 'Hero Content Saved');
      }
    } catch (error) {
      console.error('Error saving hero content:', error);
      showError('Failed to save hero content. Please try again or check your connection.', 'Save Failed');
    } finally {
      setLoading(false);
    }
  };

  const savePersonalContent = async () => {
    setLoading(true);
    try {
      const response = await fetch(`${API_BASE}/admin/content/personal`, {
        method: 'PUT',
        headers: authHeaders,
        body: JSON.stringify(personalContent)
      });
      if (response.ok) {
        showSuccess('Personal section content updated successfully! Visitors will see the new information immediately.', 'Personal Content Saved');
      }
    } catch (error) {
      console.error('Error saving personal content:', error);
      showError('Failed to save personal content. Please check your connection and try again.', 'Save Failed');
    } finally {
      setLoading(false);
    }
  };

  const saveContactContent = async () => {
    setLoading(true);
    try {
      const response = await fetch(`${API_BASE}/admin/content/contact`, {
        method: 'PUT',
        headers: authHeaders,
        body: JSON.stringify(contactContent)
      });
      if (response.ok) {
        showSuccess('Contact section updated successfully! Visitors will see the new contact information and availability status.', 'Contact Content Saved');
      }
    } catch (error) {
      console.error('Error saving contact content:', error);
      showError('Failed to save contact content. Please verify your changes and try again.', 'Save Failed');
    } finally {
      setLoading(false);
    }
  };

  const editProject = (project) => {
    // Use single state update to avoid concurrent rendering issues
    setEditorConfig({
      type: 'project',
      itemId: project.id
    });
    setShowFullScreenEditor(true);
  };

  const deleteProject = async (projectId) => {
    if (window.confirm('Are you sure you want to delete this project?')) {
      try {
        const response = await fetch(`${API_BASE}/admin/content/projects/${projectId}`, {
          method: 'DELETE',
          headers: authHeaders
        });
        if (response.ok) {
          setProjects(projects.filter(p => p.id !== projectId));
          alert('Project deleted successfully!');
        }
      } catch (error) {
        console.error('Error deleting project:', error);
        alert('Error deleting project');
      }
    }
  };

  const editBlog = (blog) => {
    // Use single state update to avoid concurrent rendering issues
    setEditorConfig({
      type: 'blog',
      itemId: blog.id
    });
    setShowFullScreenEditor(true);
  };

  const deleteBlog = async (blogId) => {
    if (window.confirm('Are you sure you want to delete this blog post?')) {
      try {
        const response = await fetch(`${API_BASE}/admin/content/blogs/${blogId}`, {
          method: 'DELETE',
          headers: authHeaders
        });
        if (response.ok) {
          setBlogs(blogs.filter(b => b.id !== blogId));
          alert('Blog post deleted successfully!');
        }
      } catch (error) {
        console.error('Error deleting blog:', error);
        alert('Error deleting blog post');
      }
    }
  };

  const renderHeroEditor = () => (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <h2 className="heading-3 text-text-primary">Hero Section Editor</h2>
        <Button onClick={saveHeroContent} className="btn-base btn-primary" disabled={loading}>
          <Save size={16} className="mr-2" />
          Save Changes
        </Button>
      </div>
      
      {heroContent && (
        <div className="grid gap-6">
          <Card className="bg-white border border-gray-200 shadow-sm p-6">
            <h3 className="heading-5 mb-4">Main Title</h3>
            <input
              type="text"
              value={heroContent.title || ''}
              onChange={(e) => setHeroContent({...heroContent, title: e.target.value})}
              className="w-full px-4 py-3 border border-gray-200 rounded-lg text-text-primary focus:outline-none focus:ring-2 focus:ring-blue-500"
              placeholder="Enter hero title..."
            />
          </Card>

          <Card className="bg-white border border-gray-200 shadow-sm p-6">
            <h3 className="heading-5 mb-4">Subtitle</h3>
            <textarea
              value={heroContent.subtitle || ''}
              onChange={(e) => setHeroContent({...heroContent, subtitle: e.target.value})}
              rows={3}
              className="w-full px-4 py-3 border border-gray-200 rounded-lg text-text-primary focus:outline-none focus:ring-2 focus:ring-blue-500"
              placeholder="Enter hero subtitle..."
            />
          </Card>

          <Card className="bg-white border border-gray-200 shadow-sm p-6">
            <h3 className="heading-5 mb-4">Ribbon Text</h3>
            <input
              type="text"
              value={heroContent.ribbon_text || ''}
              onChange={(e) => setHeroContent({...heroContent, ribbon_text: e.target.value})}
              className="w-full px-4 py-3 border border-gray-200 rounded-lg text-text-primary focus:outline-none focus:ring-2 focus:ring-blue-500"
              placeholder="Enter ribbon text..."
            />
          </Card>

          <Card className="bg-white border border-gray-200 shadow-sm p-6">
            <h3 className="heading-5 mb-4">Button Texts</h3>
            <div className="grid md:grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium mb-2">CTA Button</label>
                <input
                  type="text"
                  value={heroContent.cta_button_text || ''}
                  onChange={(e) => setHeroContent({...heroContent, cta_button_text: e.target.value})}
                  className="w-full px-4 py-3 border border-gray-200 rounded-lg text-text-primary focus:outline-none focus:ring-2 focus:ring-blue-500"
                />
              </div>
              <div>
                <label className="block text-sm font-medium mb-2">Resume Button</label>
                <input
                  type="text"
                  value={heroContent.resume_button_text || ''}
                  onChange={(e) => setHeroContent({...heroContent, resume_button_text: e.target.value})}
                  className="w-full px-4 py-3 border border-gray-200 rounded-lg text-text-primary focus:outline-none focus:ring-2 focus:ring-blue-500"
                />
              </div>
            </div>
          </Card>

          <Card className="bg-white border border-gray-200 shadow-sm p-6">
            <h3 className="heading-5 text-text-primary mb-4">Background Image</h3>
            <ImageUpload
              currentImage={heroContent.background_image}
              onImageChange={(imageUrl) => setHeroContent({...heroContent, background_image: imageUrl})}
              onImageRemove={() => setHeroContent({...heroContent, background_image: ''})}
              label="Hero Background Image"
              description="Upload a high-quality background image for the hero section"
              maxSize={10}
            />
          </Card>
        </div>
      )}
    </div>
  );

  const renderPersonalEditor = () => (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <h2 className="heading-3 text-text-primary">Personal Info Editor</h2>
        <Button onClick={savePersonalContent} className="btn-base btn-primary" disabled={loading}>
          <Save size={16} className="mr-2" />
          Save Changes
        </Button>
      </div>
      
      {personalContent && (
        <div className="grid gap-6">
          <Card className="bg-white border border-gray-200 shadow-sm p-6">
            <h3 className="heading-5 mb-4">Section Title & Subtitle</h3>
            <div className="space-y-4">
              <input
                type="text"
                value={personalContent.section_title || ''}
                onChange={(e) => setPersonalContent({...personalContent, section_title: e.target.value})}
                className="w-full px-4 py-3 border border-gray-200 rounded-lg text-text-primary focus:outline-none focus:ring-2 focus:ring-blue-500"
                placeholder="Section title..."
              />
              <textarea
                value={personalContent.section_subtitle || ''}
                onChange={(e) => setPersonalContent({...personalContent, section_subtitle: e.target.value})}
                rows={3}
                className="w-full px-4 py-3 border border-gray-200 rounded-lg text-text-primary focus:outline-none focus:ring-2 focus:ring-blue-500"
                placeholder="Section subtitle..."
              />
            </div>
          </Card>

          <Card className="bg-white border border-gray-200 shadow-sm p-6">
            <h3 className="heading-5 text-text-primary mb-4">Profile Image</h3>
            <ImageUpload
              currentImage={personalContent.profile_image}
              onImageChange={(imageUrl) => setPersonalContent({...personalContent, profile_image: imageUrl})}
              onImageRemove={() => setPersonalContent({...personalContent, profile_image: ''})}
              label="Professional Profile Photo"
              description="Upload your professional headshot or profile photo"
              maxSize={5}
            />
          </Card>

          <Card className="bg-white border border-gray-200 shadow-sm p-6">
            <h3 className="heading-5 mb-4">Skills</h3>
            <div className="grid md:grid-cols-3 gap-4">
              <div>
                <label className="block text-sm font-medium mb-2">Technical Skills</label>
                <textarea
                  value={personalContent.skills?.technical?.join(', ') || ''}
                  onChange={(e) => setPersonalContent({
                    ...personalContent, 
                    skills: {
                      ...personalContent.skills,
                      technical: e.target.value.split(', ').filter(s => s.trim())
                    }
                  })}
                  rows={4}
                  className="w-full px-4 py-3 border border-gray-200 rounded-lg text-text-primary focus:outline-none focus:ring-2 focus:ring-blue-500"
                  placeholder="Comma-separated skills..."
                />
              </div>
              <div>
                <label className="block text-sm font-medium mb-2">Domain Skills</label>
                <textarea
                  value={personalContent.skills?.domain?.join(', ') || ''}
                  onChange={(e) => setPersonalContent({
                    ...personalContent, 
                    skills: {
                      ...personalContent.skills,
                      domain: e.target.value.split(', ').filter(s => s.trim())
                    }
                  })}
                  rows={4}
                  className="w-full px-4 py-3 border border-gray-200 rounded-lg text-text-primary focus:outline-none focus:ring-2 focus:ring-blue-500"
                  placeholder="Comma-separated skills..."
                />
              </div>
              <div>
                <label className="block text-sm font-medium mb-2">Leadership Skills</label>
                <textarea
                  value={personalContent.skills?.leadership?.join(', ') || ''}
                  onChange={(e) => setPersonalContent({
                    ...personalContent, 
                    skills: {
                      ...personalContent.skills,
                      leadership: e.target.value.split(', ').filter(s => s.trim())
                    }
                  })}
                  rows={4}
                  className="w-full px-4 py-3 border border-gray-200 rounded-lg text-text-primary focus:outline-none focus:ring-2 focus:ring-blue-500"
                  placeholder="Comma-separated skills..."
                />
              </div>
            </div>
          </Card>
        </div>
      )}
    </div>
  );

  const renderProjectsEditor = () => (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <h2 className="heading-3 text-text-primary">Projects Manager</h2>
        <Button onClick={() => {
          setEditorConfig({
            type: 'project',
            itemId: 'new'
          });
          setShowFullScreenEditor(true);
        }} className="btn-base btn-primary">
          <Plus size={16} className="mr-2" />
          Add Project
        </Button>
      </div>
      
      <div className="grid md:grid-cols-2 gap-6">
        {projects.map((project) => (
          <Card key={project.id} className="bg-white border border-gray-200 shadow-sm p-6">
            <div className="flex items-start justify-between mb-4">
              <div>
                <h3 className="heading-5 text-text-primary">{project.title}</h3>
                <p className="text-sm text-text-secondary">{project.category}</p>
              </div>
              <div className="flex space-x-2">
                <Button onClick={() => editProject(project)} className="btn-base btn-secondary btn-sm">
                  <Edit3 size={14} />
                </Button>
                <Button onClick={() => deleteProject(project.id)} className="btn-base btn-rose btn-sm">
                  <Trash2 size={14} />
                </Button>
              </div>
            </div>
            <p className="text-sm text-text-secondary mb-3 line-clamp-3">{project.description}</p>
            <div className="flex flex-wrap gap-1 mb-3">
              {project.technologies?.slice(0, 3).map((tech, index) => (
                <Badge key={index} className="text-xs bg-blue-100 text-blue-700">{tech}</Badge>
              ))}
            </div>
            <div className="flex items-center justify-between">
              <Badge className={`text-xs ${
                project.status === 'Completed' ? 'bg-green-100 text-green-700' : 'bg-yellow-100 text-yellow-700'
              }`}>
                {project.status}
              </Badge>
              <span className="text-xs text-gray-500">{new Date(project.created_at).toLocaleDateString()}</span>
            </div>
          </Card>
        ))}
      </div>
    </div>
  );

  const renderBlogsEditor = () => (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <h2 className="heading-3 text-text-primary">Blog Posts Manager</h2>
        <Button onClick={() => {
          setEditorConfig({
            type: 'blog',
            itemId: 'new'
          });
          setShowFullScreenEditor(true);
        }} className="btn-base btn-primary">
          <Plus size={16} className="mr-2" />
          Add Blog Post
        </Button>
      </div>
      
      <div className="grid gap-6">
        {blogs.map((blog) => (
          <Card key={blog.id} className="bg-white border border-gray-200 shadow-sm p-6">
            <div className="flex items-start justify-between mb-4">
              <div className="flex-1">
                <h3 className="heading-5 text-text-primary">{blog.title}</h3>
                <p className="text-sm text-text-secondary mt-1">{blog.excerpt}</p>
                <div className="flex items-center space-x-4 mt-3">
                  <Badge className="text-xs bg-purple-100 text-purple-700">{blog.category}</Badge>
                  <Badge className={`text-xs ${
                    blog.published ? 'bg-green-100 text-green-700' : 'bg-gray-100 text-gray-700'
                  }`}>
                    {blog.published ? 'Published' : 'Draft'}
                  </Badge>
                  <span className="text-xs text-gray-500">{new Date(blog.created_at).toLocaleDateString()}</span>
                </div>
              </div>
              <div className="flex space-x-2">
                <Button onClick={() => editBlog(blog)} className="btn-base btn-secondary btn-sm">
                  <Edit3 size={14} />
                </Button>
                <Button onClick={() => deleteBlog(blog.id)} className="btn-base btn-rose btn-sm">
                  <Trash2 size={14} />
                </Button>
              </div>
            </div>
          </Card>
        ))}
      </div>
    </div>
  );

  const renderContactEditor = () => (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <h2 className="heading-3 text-text-primary">Contact Info Editor</h2>
        <Button onClick={saveContactContent} className="btn-base btn-primary" disabled={loading}>
          <Save size={16} className="mr-2" />
          Save Changes
        </Button>
      </div>
      
      {contactContent && (
        <div className="grid gap-6">
          <Card className="bg-white border border-gray-200 shadow-sm p-6">
            <h3 className="heading-5 mb-4">Section Content</h3>
            <div className="space-y-4">
              <input
                type="text"
                value={contactContent.section_title || ''}
                onChange={(e) => setContactContent({...contactContent, section_title: e.target.value})}
                className="w-full px-4 py-3 border border-gray-200 rounded-lg text-text-primary focus:outline-none focus:ring-2 focus:ring-blue-500"
                placeholder="Section title..."
              />
              <textarea
                value={contactContent.section_subtitle || ''}
                onChange={(e) => setContactContent({...contactContent, section_subtitle: e.target.value})}
                rows={3}
                className="w-full px-4 py-3 border border-gray-200 rounded-lg text-text-primary focus:outline-none focus:ring-2 focus:ring-blue-500"
                placeholder="Section subtitle..."
              />
              <input
                type="url"
                value={contactContent.linkedin_url || ''}
                onChange={(e) => setContactContent({...contactContent, linkedin_url: e.target.value})}
                className="w-full px-4 py-3 border border-gray-200 rounded-lg text-text-primary focus:outline-none focus:ring-2 focus:ring-blue-500"
                placeholder="LinkedIn URL..."
              />
            </div>
          </Card>

          <Card className="bg-white border border-gray-200 shadow-sm p-6">
            <h3 className="heading-5 mb-4">Availability Status</h3>
            <div className="space-y-4">
              <input
                type="text"
                value={contactContent.availability_status || ''}
                onChange={(e) => setContactContent({...contactContent, availability_status: e.target.value})}
                className="w-full px-4 py-3 border border-gray-200 rounded-lg text-text-primary focus:outline-none focus:ring-2 focus:ring-blue-500"
                placeholder="Availability status..."
              />
              <textarea
                value={contactContent.availability_text || ''}
                onChange={(e) => setContactContent({...contactContent, availability_text: e.target.value})}
                rows={3}
                className="w-full px-4 py-3 border border-gray-200 rounded-lg text-text-primary focus:outline-none focus:ring-2 focus:ring-blue-500"
                placeholder="Availability description..."
              />
            </div>
          </Card>
        </div>
      )}
    </div>
  );

  const renderAnalytics = () => (
    <div className="space-y-6">
      <h2 className="heading-3 text-text-primary">Website Analytics & Settings</h2>
      
      {/* Ad Settings */}
      <Card className="bg-white border border-gray-200 shadow-sm p-6">
        <h3 className="heading-5 mb-4">Ad Settings</h3>
        <div className="space-y-4">
          <div className="flex items-center justify-between p-4 bg-gray-50 rounded-lg">
            <div>
              <h4 className="font-semibold text-text-primary">Google AdSense Ads</h4>
              <p className="text-sm text-text-secondary">Toggle advertisements on/off across the website</p>
            </div>
            <button
              onClick={toggleAds}
              className={`w-12 h-6 rounded-full flex items-center px-1 transition-colors ${
                adsEnabled ? 'bg-green-500 justify-end' : 'bg-gray-300 justify-start'
              }`}
            >
              <div className="w-4 h-4 bg-white rounded-full"></div>
            </button>
          </div>
          <p className="text-xs text-gray-600">
            Current Status: <span className={adsEnabled ? 'text-green-600' : 'text-red-600'}>
              {adsEnabled ? 'Ads Enabled' : 'Ads Disabled'}
            </span>
          </p>
        </div>
      </Card>

      {/* Analytics */}
      {analytics && (
        <div className="grid md:grid-cols-2 gap-6">
          <Card className="bg-white border border-gray-200 shadow-sm p-6">
            <div className="flex items-center justify-between">
              <div>
                <h3 className="heading-5 text-text-primary">Total Visitors</h3>
                <p className="text-3xl font-bold text-blue-600 mt-2">{analytics.total_visitors}</p>
              </div>
              <Users size={40} className="text-blue-600" />
            </div>
          </Card>

          <Card className="bg-white border border-gray-200 shadow-sm p-6">
            <div className="flex items-center justify-between">
              <div>
                <h3 className="heading-5 text-text-primary">Unique Visitors</h3>
                <p className="text-3xl font-bold text-green-600 mt-2">{analytics.unique_visitors}</p>
              </div>
              <Eye size={40} className="text-green-600" />
            </div>
          </Card>
        </div>
      )}

      {analytics?.recent_visitors && (
        <Card className="bg-white border border-gray-200 shadow-sm p-6">
          <h3 className="heading-5 mb-4">Recent Visitors</h3>
          <div className="space-y-3">
            {analytics.recent_visitors.map((visitor, index) => (
              <div key={index} className="flex items-center justify-between py-2 border-b border-gray-100">
                <div>
                  <p className="text-sm font-medium">{visitor.page_visited}</p>
                  <p className="text-xs text-gray-500">{visitor.ip_address}</p>
                </div>
                <div className="text-right">
                  <p className="text-xs text-gray-500">
                    {new Date(visitor.timestamp).toLocaleString()}
                  </p>
                </div>
              </div>
            ))}
          </div>
        </Card>
      )}
    </div>
  );

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Header */}
      <header className="bg-white shadow-sm border-b border-gray-200">
        <div className="container mx-auto px-6 py-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center space-x-4">
              <Shield size={32} className="text-blue-600" />
              <div>
                <h1 className="heading-4 text-text-primary">Admin Dashboard</h1>
                <p className="text-sm text-gray-600">Content Management System</p>
              </div>
            </div>
            
            <div className="flex items-center space-x-4">
              <div className="flex items-center space-x-2">
                <img src={user?.picture} alt={user?.name} className="w-8 h-8 rounded-full" />
                <span className="text-sm font-medium">{user?.name}</span>
              </div>
              <Button onClick={onLogout} className="btn-base btn-secondary btn-sm">
                <LogOut size={16} className="mr-2" />
                Logout
              </Button>
            </div>
          </div>
        </div>
      </header>

      <div className="container mx-auto px-6 py-8">
        <div className="grid lg:grid-cols-5 gap-8">
          {/* Sidebar */}
          <div className="lg:col-span-1">
            <Card className="bg-white border border-gray-200 shadow-sm p-4">
              <nav className="space-y-2">
                {[
                  { id: 'hero', label: 'Hero Section', icon: Settings },
                  { id: 'personal', label: 'Personal Info', icon: Users },
                  { id: 'projects', label: 'Projects', icon: FileText },
                  { id: 'blogs', label: 'Blog Posts', icon: Edit3 },
                  { id: 'contact', label: 'Contact Info', icon: Settings },
                  { id: 'analytics', label: 'Analytics', icon: BarChart3 }
                ].map((item) => {
                  const Icon = item.icon;
                  return (
                    <button
                      key={item.id}
                      onClick={() => setActiveTab(item.id)}
                      className={`w-full flex items-center space-x-3 px-4 py-3 rounded-lg text-left transition-all duration-200 ${
                        activeTab === item.id
                          ? 'bg-blue-100 text-blue-700 font-medium'
                          : 'text-gray-600 hover:bg-gray-100'
                      }`}
                    >
                      <Icon size={20} />
                      <span>{item.label}</span>
                    </button>
                  );
                })}
              </nav>
            </Card>
          </div>

          {/* Main Content */}
          <div className="lg:col-span-4">
            {loading && (
              <div className="text-center py-8">
                <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600 mx-auto"></div>
                <p className="text-gray-600 mt-2">Loading...</p>
              </div>
            )}
            
            {!loading && (
              <>
                {activeTab === 'hero' && renderHeroEditor()}
                {activeTab === 'personal' && renderPersonalEditor()}
                {activeTab === 'projects' && renderProjectsEditor()}
                {activeTab === 'blogs' && renderBlogsEditor()}
                {activeTab === 'contact' && renderContactEditor()}
                {activeTab === 'analytics' && renderAnalytics()}
              </>
            )}
          </div>
        </div>
      </div>

      {/* Full Screen Editor */}
      {showFullScreenEditor && (
        <FullScreenEditor
          type={editorConfig.type}
          itemId={editorConfig.itemId}
          onClose={() => {
            setShowFullScreenEditor(false);
            setEditorConfig({
              type: '',
              itemId: ''
            });
          }}
          onSave={editorConfig.type === 'blog' ? saveBlog : saveProject}
        />
      )}
    </div>
  );
};

export default AdminDashboard;